<script lang="ts">
  import { onDestroy, onMount } from "svelte";
  import MessageModal from "./messageModal.svelte";
  import Button from "./shared/Button.svelte";
  import UpgradeManager from "./UpgradeManager.svelte";
  import {
    ensureHubspotAuthenticated,
    fetchJsonWithHubspotAuth,
    HUBSPOT_AUTH_CHANGED_EVENT,
    isHubspotAuthRequiredError,
    openHubspotAuthDialog,
  } from "../lib/hubspotAuth";

  let confirmReset = false;
  let confirmCatalogSwitch = false;
  type SettingsTab = "aws" | "hubspot" | "stageMapping" | "additional" | "sync" | "bulksync" | "hubspotScheduler" | "cleanup" | "hubspotCard" | "ai" | "upgrade";
  let activeTab: SettingsTab = "aws";
  let isSettingsNavCollapsed = false;
  const SETTINGS_NAV_COLLAPSED_KEY = "acelinkhs-settings-nav-collapsed";

  // HubSpot Card Setup state
  const CARD_DEPLOY_AND_INSTALL_URL = `${process.env.SVELTE_APP_REMOTE_URL}/api/hubspot-auth/setup-card/install`;
  const CARD_INSTALL_URL_ENDPOINT = `${process.env.SVELTE_APP_REMOTE_URL}/api/hubspot-auth/setup-card/install-url`;
  const DEFAULT_PROJECT_NAME = "alhs-acelink-connect";
  let cardSetupCredential = "";
  let cardSetupPortalId = "";
  let cardSetupProjectName = DEFAULT_PROJECT_NAME;
  let cardSetupDomainUrl = "";
  let cardDeployLoading = false;
  let cardInstallLoading = false;
  let cardSetupInstallUrl = "";
  let cardSetupError = "";
  let cardSetupSuccess = "";
  let cardProjectUploaded = false;

  async function deployCard() {
    cardSetupError = "";
    cardSetupSuccess = "";
    cardSetupInstallUrl = "";
    cardProjectUploaded = false;

    if (!cardSetupCredential.trim()) {
      cardSetupError = "HubSpot Personal Access Key or bearer token is required.";
      return;
    }
    if (!cardSetupPortalId.trim()) {
      cardSetupError = "Portal ID is required.";
      return;
    }

    const installWindow = window.open("", "_blank");
    installWindow?.document.write("<p style=\"font-family: sans-serif; padding: 24px;\">Preparing HubSpot install…</p>");
    installWindow?.document.close();

    cardDeployLoading = true;
    try {
      const res = await fetch(CARD_DEPLOY_AND_INSTALL_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          hubspotCredential: cardSetupCredential.trim(),
          portalId: cardSetupPortalId.trim(),
          projectName: cardSetupProjectName.trim() || DEFAULT_PROJECT_NAME,
          cardDomainUrl: cardSetupDomainUrl.trim() || undefined,
          cardCallbackUrl: window.location.origin || undefined,
        }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      cardSetupInstallUrl = data.installUrl;
      cardProjectUploaded = true;
      cardSetupSuccess =
        data.message ||
        "Project uploaded. Finish the HubSpot install flow in the opened window.";
      if (installWindow) {
        installWindow.location.replace(cardSetupInstallUrl);
      } else {
        window.location.assign(cardSetupInstallUrl);
      }
    } catch (err) {
      installWindow?.close();
      cardSetupError = err instanceof Error ? err.message : "Something went wrong.";
    } finally {
      cardDeployLoading = false;
    }
  }

  async function openInstallPage() {
    cardSetupError = "";
    cardSetupSuccess = "";
    cardSetupInstallUrl = "";

    if (!cardProjectUploaded) {
      cardSetupError = "Upload the card to HubSpot first so the install page points to a ready project.";
      return;
    }
    if (!cardSetupPortalId.trim()) {
      cardSetupError = "Portal ID is required.";
      return;
    }

    cardInstallLoading = true;
    try {
      const res = await fetch(CARD_INSTALL_URL_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          portalId: cardSetupPortalId.trim(),
          projectName: cardSetupProjectName.trim() || DEFAULT_PROJECT_NAME,
          cardDomainUrl: cardSetupDomainUrl.trim() || undefined,
          cardCallbackUrl: window.location.origin || undefined,
        }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      cardSetupInstallUrl = data.installUrl;
      cardSetupSuccess =
        data.message ||
        "Open the HubSpot project page and click \"Install now\" to display the card on your portal.";
      window.open(cardSetupInstallUrl, "_blank");
    } catch (err) {
      cardSetupError = err instanceof Error ? err.message : "Something went wrong.";
    } finally {
      cardInstallLoading = false;
    }
  }

  let hasHubspotSettings: boolean = true;

  const settingsSections: Array<{
    id: SettingsTab;
    label: string;
    description: string;
    iconPath: string;
    requiresHubspot?: boolean;
  }> = [
    { id: "aws", label: "AWS", description: "Credentials and catalog setup", iconPath: "M17.5 19H8a5 5 0 1 1 1.1-9.88A6 6 0 0 1 20.5 12.5 3.5 3.5 0 0 1 17.5 19Z" },
    { id: "sync", label: "Sync", description: "Stage and duration filters", iconPath: "M17 1l4 4-4 4M3 11V9a4 4 0 0 1 4-4h14M7 23l-4-4 4-4M21 13v2a4 4 0 0 1-4 4H3" },
    { id: "hubspot", label: "HubSpot", description: "CRM integration settings", iconPath: "M7 12a5 5 0 1 0 5-5M12 7V3M12 3h4M12 17v4M12 21H8M17 12h4M21 12v4", requiresHubspot: true },
    { id: "stageMapping", label: "Stage Mapping", description: "HubSpot and AWS stage alignment", iconPath: "M8 7h11l-3-3M16 17H5l3 3M19 7l-3 3M5 17l3-3", requiresHubspot: true },
    { id: "bulksync", label: "Bulk Sync", description: "HubSpot and AWS automation", iconPath: "M12 8v5l3 2M21 12a9 9 0 1 1-9-9" },
    { id: "hubspotScheduler", label: "Scheduler", description: "HubSpot polling and feedback", iconPath: "M8 7V3M16 7V3M4 11h16M5 5h14a1 1 0 0 1 1 1v14H4V6a1 1 0 0 1 1-1ZM9 15h2M13 15h2" },
    { id: "cleanup", label: "Cleanup", description: "Finished opportunity cleanup", iconPath: "M3 6h18M8 6V4h8v2M6 6l1 16h10l1-16M10 11v6M14 11v6" },
    { id: "hubspotCard", label: "Card Setup", description: "HubSpot CRM card install", iconPath: "M4 5h16v14H4zM8 9h8M8 13h5" },
    { id: "ai", label: "AI", description: "Provider, model, and MCP", iconPath: "M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8L12 3ZM18 15l.8 2.2L21 18l-2.2.8L18 21l-.8-2.2L15 18l2.2-.8L18 15Z" },
    { id: "upgrade", label: "Updates", description: "Production version and upgrades", iconPath: "M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" },
    { id: "additional", label: "Advanced", description: "Additional settings", iconPath: "M4 6h16M7 6v12M4 18h16M17 6v12M10 12h4" },
  ];

  $: visibleSettingsSections = settingsSections.filter(
    (section) => !section.requiresHubspot || hasHubspotSettings
  );
  $: activeSettingsSection =
    visibleSettingsSections.find((section) => section.id === activeTab) ?? visibleSettingsSections[0];

  const wideSettingsFields = new Set(["syncStages", "mcpServer", "hubspotScopes", "hubspotWebhookUrl", "alwaysFillAwsMandatoryFields", "defaultMandatoryFieldContext"]);

  const API_URL = `${process.env.SVELTE_APP_FE_REMOTE_URL}/api/alace/configurations`;
  const SAVE_SETTINGS_URL = `${process.env.SVELTE_APP_REMOTE_URL}/api/alace/sync/catalog-settings`;
  const SETTINGS_URL = `${process.env.SVELTE_APP_FE_REMOTE_URL}/api/hubspot-config/settings`;
  const STAGES_URL = `${process.env.SVELTE_APP_ALADV_BASE_URL}/aladv/stages`;
  const DURATIONS_URL = `${process.env.SVELTE_APP_ALADV_BASE_URL}/aladv/durations`;
  const AI_PROVIDERS_URL = `${process.env.SVELTE_APP_ALADV_BASE_URL}/aladv/ai-providers`;
  const AI_MODELS_URL = `${process.env.SVELTE_APP_REMOTE_URL}/api/alace/ai-models`;
  const WEBHOOK_BASE = `${process.env.SVELTE_APP_REMOTE_URL}/api/hubspot-webhook`;
  const BULK_SYNC_URL = `${process.env.SVELTE_APP_REMOTE_URL}/api/bulk-sync`;
  const BULK_SYNC_AWS_URL = `${process.env.SVELTE_APP_REMOTE_URL}/api/alace/bulk-sync`;
  const HUBSPOT_SYNC_SCHEDULER_URL = `${process.env.SVELTE_APP_REMOTE_URL}/api/hubspot-sync-scheduler`;
  const HUBSPOT_SYNC_RUN_URL = `${process.env.SVELTE_APP_REMOTE_URL}/api/hubspot-sync/scheduler/run`;
  const HUBSPOT_PIPELINES_URL = `${process.env.SVELTE_APP_REMOTE_URL}/api/hubspot-sync/pipelines/deals`;
  const FINISHED_OPPORTUNITIES_CLEANUP_URL = `${process.env.SVELTE_APP_REMOTE_URL}/api/alace/cron/jobs/cleanup-finished-opportunities`;
  const ALADV_UNAVAILABLE_MESSAGE =
    "Validation settings service is temporarily unavailable. Please try again later.";
  const DEFAULT_ALADV_REQUEST_TIMEOUT_MS = 15000;

  const AWS_SALES_STAGES = [
    "Prospect",
    "Qualified",
    "Technical Validation",
    "Business Validation",
    "Committed",
    "Launched",
    "Closed Lost",
  ];
  const RECOMMENDED_STAGE_LABELS: Record<string, string> = {
    Prospect: "Appointment Scheduled",
    Qualified: "Qualified To Buy",
    "Technical Validation": "Presentation Scheduled",
    "Business Validation": "Decision Maker Bought-In",
    Committed: "Contract Sent",
    Launched: "Closed Won",
    "Closed Lost": "Closed Lost",
  };
  type HubspotPipeline = {
    id: string;
    label: string;
    stages: Array<{ id: string; label: string }>;
  };
  type StageMappingRow = { pipelineId: string; hubspotStageId: string; awsStage: string };
  let hubspotPipelines: HubspotPipeline[] = [];
  let stageMappings: StageMappingRow[] = AWS_SALES_STAGES.map((awsStage) => ({
    pipelineId: "",
    hubspotStageId: "",
    awsStage,
  }));
  let autoPropagateStages = false;
  let stageMappingsLoading = false;
  let stageMappingsError = "";
  let pipelinesError = "";
  let hubspotAuthChecked = false;
  let isHubspotAuthenticated = false;

  const loadStageMapping = () => {
    try {
      autoPropagateStages = config.awsStageAutoPropagation === true || config.awsStageAutoPropagation === "true";
      const savedMappings = JSON.parse(config.awsStageMappings || "[]");
      const byStage = new Map(savedMappings.map((mapping: any) => [mapping.awsStage, mapping]));
      stageMappings = AWS_SALES_STAGES.map((awsStage) => {
        const mapping: any = byStage.get(awsStage);
        return {
          awsStage,
          pipelineId: String(mapping?.pipelineId ?? ""),
          hubspotStageId: String(mapping?.hubspotStageId ?? ""),
        };
      });
    } catch {
      autoPropagateStages = false;
      stageMappings = AWS_SALES_STAGES.map((awsStage) => ({ awsStage, pipelineId: "", hubspotStageId: "" }));
      stageMappingsError = "Failed to load stage mapping configuration.";
    }
  };

  const fetchHubspotPipelines = async () => {
    stageMappingsLoading = true;
    pipelinesError = "";
    try {
      const { response, data, text } = await fetchJsonWithHubspotAuth(HUBSPOT_PIPELINES_URL, undefined, {
        authRequiredMessage: "Sign in to HubSpot to load deal pipelines.",
      });
      if (!response.ok) {
        throw new Error(data?.error || text || "Failed to load HubSpot deal pipelines.");
      }
      hubspotPipelines = Array.isArray(data?.pipelines) ? data.pipelines : [];
    } catch (err) {
      if (isHubspotAuthRequiredError(err)) isHubspotAuthenticated = false;
      pipelinesError = isHubspotAuthRequiredError(err)
        ? ""
        : err instanceof Error ? err.message : "Failed to load HubSpot deal pipelines.";
      hubspotPipelines = [];
    } finally {
      stageMappingsLoading = false;
    }
  };

  const refreshHubspotAuthStatus = async () => {
    isHubspotAuthenticated = await ensureHubspotAuthenticated().catch(() => false);
    hubspotAuthChecked = true;
    if (isHubspotAuthenticated && activeTab === "stageMapping" && hubspotPipelines.length === 0) {
      await fetchHubspotPipelines();
    }
  };

  const connectHubspot = () => {
    openHubspotAuthDialog("Sign in to HubSpot to load deal pipelines.");
  };

  const stageSelectionValue = (row: StageMappingRow) =>
    row.pipelineId && row.hubspotStageId ? `${row.pipelineId}::${row.hubspotStageId}` : "";

  const setStageSelection = (awsStage: string, value: string) => {
    const [pipelineId = "", hubspotStageId = ""] = value.split("::");
    stageMappings = stageMappings.map((row) =>
      row.awsStage === awsStage ? { ...row, pipelineId, hubspotStageId } : row
    );
    stageMappingsError = "";
  };

  const fillRecommendedStageMapping = () => {
    const normalize = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, "");
    const pipelinesByMatches = hubspotPipelines.map((pipeline) => ({
      pipeline,
      matches: Object.values(RECOMMENDED_STAGE_LABELS).filter((label) =>
        pipeline.stages.some((stage) => normalize(stage.label) === normalize(label))
      ).length,
    }));
    const selected = pipelinesByMatches.sort((a, b) => b.matches - a.matches)[0];
    if (!selected?.matches) {
      stageMappingsError = "No recommended HubSpot deal stages were found.";
      return;
    }
    stageMappings = stageMappings.map((row) => {
      const label = RECOMMENDED_STAGE_LABELS[row.awsStage];
      const stage = selected.pipeline.stages.find((item) => normalize(item.label) === normalize(label));
      return stage
        ? { ...row, pipelineId: selected.pipeline.id, hubspotStageId: stage.id }
        : row;
    });
    stageMappingsError = "";
    saveMessage = `Filled ${selected.matches} recommended stage mappings. Review and save the configuration.`;
    saveMessageType = "success";
  };

  const validateStageMapping = () => {
    if (!autoPropagateStages) return;
    if (stageMappings.some((row) => !row.pipelineId || !row.hubspotStageId)) {
      throw new Error("The stage mapping is incomplete. Assign a HubSpot deal stage to every AWS sales stage.");
    }
    if (new Set(stageMappings.map((row) => row.pipelineId)).size !== 1) {
      throw new Error("All mapped stages must belong to the same HubSpot pipeline.");
    }
    if (new Set(stageMappings.map((row) => row.hubspotStageId)).size !== stageMappings.length) {
      throw new Error("Each HubSpot deal stage can only be mapped once.");
    }
  };

  const applyStageMappingToConfig = () => {
    validateStageMapping();
    config.awsStageAutoPropagation = autoPropagateStages;
    config.awsStageMappings = JSON.stringify(
      stageMappings.filter((row) => row.pipelineId && row.hubspotStageId)
    );
  };

  // Bulk Sync state
  type BulkSyncForm = {
    schedule: string;
    timezone: string;
    enabled: boolean;
    minute: string;
    hour: string;
    dayOfMonth: string;
    month: string;
    dayOfWeek: string;
    loading: boolean;
    saving: boolean;
    message: string;
    messageType: "success" | "error";
    synced: boolean;
    runningSchedule: string;
    runningEnabled: boolean;
  };

  type BulkSyncTarget = "partner" | "aws" | "cleanup";

  let bulkSyncForm: BulkSyncForm = {
    schedule: "",
    timezone: "",
    enabled: false,
    minute: "",
    hour: "",
    dayOfMonth: "",
    month: "",
    dayOfWeek: "",
    loading: false,
    saving: false,
    message: "",
    messageType: "success",
    synced: true,
    runningSchedule: "",
    runningEnabled: false,
  };

  let awsBulkSyncForm: BulkSyncForm = {
    schedule: "",
    timezone: "",
    enabled: false,
    minute: "",
    hour: "",
    dayOfMonth: "",
    month: "",
    dayOfWeek: "",
    loading: false,
    saving: false,
    message: "",
    messageType: "success",
    synced: true,
    runningSchedule: "",
    runningEnabled: false,
  };

  let hubspotSchedulerForm: BulkSyncForm = {
    schedule: "",
    timezone: "",
    enabled: false,
    minute: "",
    hour: "",
    dayOfMonth: "",
    month: "",
    dayOfWeek: "",
    loading: false,
    saving: false,
    message: "",
    messageType: "success",
    synced: true,
    runningSchedule: "",
    runningEnabled: false,
  };

  let cleanupSchedulerForm: BulkSyncForm = {
    schedule: "",
    timezone: "",
    enabled: false,
    minute: "",
    hour: "",
    dayOfMonth: "",
    month: "",
    dayOfWeek: "",
    loading: false,
    saving: false,
    message: "",
    messageType: "success",
    synced: true,
    runningSchedule: "",
    runningEnabled: false,
  };

  const parseSchedule = (schedule: string, defaults: { minute: string; hour: string; dayOfMonth: string; month: string; dayOfWeek: string }) => {
    if (!schedule) return defaults;
    const parts = schedule.split(/\s+/);
    if (parts.length !== 5) return defaults;
    return {
      minute: parts[0],
      hour: parts[1],
      dayOfMonth: parts[2],
      month: parts[3],
      dayOfWeek: parts[4],
    };
  };

  const buildSchedule = (minute: string, hour: string, dayOfMonth: string, month: string, dayOfWeek: string): string => {
    return `${minute || "*"} ${hour || "*"} ${dayOfMonth || "*"} ${month || "*"} ${dayOfWeek || "*"}`;
  };

  const getBulkForm = (target: BulkSyncTarget) => {
    if (target === "aws") return awsBulkSyncForm;
    if (target === "cleanup") return cleanupSchedulerForm;
    return bulkSyncForm;
  };

  const setBulkForm = (target: BulkSyncTarget, next: BulkSyncForm) => {
    if (target === "aws") {
      awsBulkSyncForm = next;
    } else if (target === "cleanup") {
      cleanupSchedulerForm = next;
    } else {
      bulkSyncForm = next;
    }
  };

  const bulkBaseUrl = (target: BulkSyncTarget) => {
    if (target === "aws") return BULK_SYNC_AWS_URL;
    if (target === "cleanup") return FINISHED_OPPORTUNITIES_CLEANUP_URL;
    return BULK_SYNC_URL;
  };

  const fetchBulkSyncConfig = async (target: BulkSyncTarget) => {
    const current = getBulkForm(target);
    setBulkForm(target, { ...current, loading: true, message: "" });
    try {
      const res = await fetch(`${bulkBaseUrl(target)}/config`);
      if (!res.ok) throw new Error("Failed to fetch bulk sync config");
      const data = await res.json();

      const dbConfig = data?.db;
      const runningConfig = data?.running;
      const synced = data?.synced ?? (dbConfig && runningConfig
        ? dbConfig.schedule === runningConfig.schedule &&
          dbConfig.timezone === runningConfig.timezone &&
          dbConfig.enabled === runningConfig.enabled
        : true);

      const scheduleValue = dbConfig?.schedule || "";
      const parsed = parseSchedule(scheduleValue, {
        minute: "",
        hour: "",
        dayOfMonth: "",
        month: "",
        dayOfWeek: "",
      });
      setBulkForm(target, {
        ...getBulkForm(target),
        schedule: scheduleValue,
        timezone: dbConfig?.timezone || "",
        enabled: dbConfig?.enabled ? dbConfig.enabled > 0 : false,
        synced,
        runningSchedule: runningConfig?.schedule || "",
        runningEnabled: runningConfig?.enabled ? runningConfig.enabled > 0 : false,
        ...parsed,
      });
    } catch (err) {
      console.error("Error loading bulk sync config:", err);
      setBulkForm(target, {
        ...getBulkForm(target),
        message: "Failed to load bulk sync config",
        messageType: "error",
      });
    } finally {
      setBulkForm(target, { ...getBulkForm(target), loading: false });
    }
  };

  const saveBulkSyncConfig = async (target: BulkSyncTarget) => {
    const current = getBulkForm(target);
    setBulkForm(target, { ...current, saving: true, message: "" });
    try {
      const schedule = buildSchedule(
        current.minute,
        current.hour,
        current.dayOfMonth,
        current.month,
        current.dayOfWeek
      );
      const payload = {
        schedule,
        timezone: current.timezone,
        enabled: current.enabled ? 1 : 0,
      };

      const res = await fetch(`${bulkBaseUrl(target)}/config`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok || data?.success === false) {
        throw new Error(typeof data?.error === "string" ? data.error : (target === "cleanup" ? "Failed to save cleanup scheduler config" : "Failed to save bulk sync config"));
      }
      if (data?.config) {
        const updatedSchedule = data.config.schedule || schedule;
        const parsed = parseSchedule(updatedSchedule, {
          minute: current.minute,
          hour: current.hour,
          dayOfMonth: current.dayOfMonth,
          month: current.month,
          dayOfWeek: current.dayOfWeek,
        });
        setBulkForm(target, {
          ...getBulkForm(target),
          schedule: updatedSchedule,
          timezone: data.config.timezone || current.timezone,
          enabled: data.config.enabled ? data.config.enabled > 0 : current.enabled,
          ...parsed,
        });
      } else {
        setBulkForm(target, { ...getBulkForm(target), schedule });
      }

      const msg = target === "cleanup"
        ? "Cleanup schedule saved."
        : getBulkForm(target).enabled
          ? "Schedule saved. Sync started in background."
          : "Schedule saved.";
      setBulkForm(target, {
        ...getBulkForm(target),
        message: msg,
        messageType: "success",
        synced: true,
        runningSchedule: getBulkForm(target).schedule,
        runningEnabled: getBulkForm(target).enabled,
      });
    } catch (err) {
      const isCleanup = target === "cleanup";
      console.error(isCleanup ? "Cleanup scheduler save failed:" : "Bulk sync save failed:", err);
      const message = err instanceof Error ? err.message : (isCleanup ? "Failed to save cleanup scheduler settings" : "Failed to save bulk sync settings");
      setBulkForm(target, {
        ...getBulkForm(target),
        message,
        messageType: "error",
      });
    } finally {
      setBulkForm(target, { ...getBulkForm(target), saving: false });
    }
  };

  const saveAllBulkSyncConfigs = async () => {
    await saveBulkSyncConfig("partner");
    await saveBulkSyncConfig("aws");
  };

  const fetchHubspotSchedulerConfig = async () => {
    hubspotSchedulerForm = { ...hubspotSchedulerForm, loading: true, message: "" };
    try {
      const res = await fetch(`${HUBSPOT_SYNC_SCHEDULER_URL}/config`);
      if (!res.ok) throw new Error("Failed to fetch hubspot scheduler config");
      const data = await res.json();

      const dbConfig = data?.db;
      const runningConfig = data?.running;
      const synced = data?.synced ?? (dbConfig && runningConfig
        ? dbConfig.schedule === runningConfig.schedule &&
          dbConfig.timezone === runningConfig.timezone &&
          dbConfig.enabled === runningConfig.enabled
        : true);

      const scheduleValue = dbConfig?.schedule || "";
      const parsed = parseSchedule(scheduleValue, {
        minute: "",
        hour: "",
        dayOfMonth: "",
        month: "",
        dayOfWeek: "",
      });

      hubspotSchedulerForm = {
        ...hubspotSchedulerForm,
        schedule: scheduleValue,
        timezone: dbConfig?.timezone || "",
        enabled: dbConfig?.enabled ? dbConfig.enabled > 0 : false,
        synced,
        runningSchedule: runningConfig?.schedule || "",
        runningEnabled: runningConfig?.enabled ? runningConfig.enabled > 0 : false,
        ...parsed,
      };
    } catch (err) {
      console.error("Error loading HubSpot scheduler config:", err);
      hubspotSchedulerForm = {
        ...hubspotSchedulerForm,
        message: "Failed to load HubSpot scheduler config",
        messageType: "error",
      };
    } finally {
      hubspotSchedulerForm = { ...hubspotSchedulerForm, loading: false };
    }
  };

  const saveHubspotSchedulerConfig = async () => {
    hubspotSchedulerForm = { ...hubspotSchedulerForm, saving: true, message: "" };
    try {
      const schedule = buildSchedule(
        hubspotSchedulerForm.minute,
        hubspotSchedulerForm.hour,
        hubspotSchedulerForm.dayOfMonth,
        hubspotSchedulerForm.month,
        hubspotSchedulerForm.dayOfWeek
      );
      const payload = {
        schedule,
        timezone: hubspotSchedulerForm.timezone,
        enabled: hubspotSchedulerForm.enabled ? 1 : 0,
      };

      const res = await fetch(`${HUBSPOT_SYNC_SCHEDULER_URL}/config`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok || data?.success === false) {
        throw new Error(data?.error || "Failed to save HubSpot scheduler config");
      }

      if (data?.config) {
        const updatedSchedule = data.config.schedule || schedule;
        const parsed = parseSchedule(updatedSchedule, {
          minute: hubspotSchedulerForm.minute,
          hour: hubspotSchedulerForm.hour,
          dayOfMonth: hubspotSchedulerForm.dayOfMonth,
          month: hubspotSchedulerForm.month,
          dayOfWeek: hubspotSchedulerForm.dayOfWeek,
        });
        hubspotSchedulerForm = {
          ...hubspotSchedulerForm,
          schedule: updatedSchedule,
          timezone: data.config.timezone || hubspotSchedulerForm.timezone,
          enabled: data.config.enabled ? data.config.enabled > 0 : hubspotSchedulerForm.enabled,
          ...parsed,
        };
      } else {
        hubspotSchedulerForm = { ...hubspotSchedulerForm, schedule };
      }

      const msg = hubspotSchedulerForm.enabled
        ? "Scheduler saved. HubSpot changes will be synced automatically."
        : "Scheduler saved.";
      hubspotSchedulerForm = {
        ...hubspotSchedulerForm,
        message: msg,
        messageType: "success",
        synced: true,
        runningSchedule: hubspotSchedulerForm.schedule,
        runningEnabled: hubspotSchedulerForm.enabled,
      };
    } catch (err) {
      console.error("HubSpot scheduler save failed:", err);
      const message = err instanceof Error ? err.message : "Failed to save HubSpot scheduler settings";
      hubspotSchedulerForm = {
        ...hubspotSchedulerForm,
        message,
        messageType: "error",
      };
    } finally {
      hubspotSchedulerForm = { ...hubspotSchedulerForm, saving: false };
    }
  };

  const runHubspotSchedulerNow = async () => {
    hubspotSchedulerForm = { ...hubspotSchedulerForm, saving: true, message: "" };
    try {
      const res = await fetch(HUBSPOT_SYNC_RUN_URL, { method: "POST" });
      const data = await res.json();
      if (!res.ok || data?.success === false) throw new Error(data?.error || "Run failed");

      hubspotSchedulerForm = {
        ...hubspotSchedulerForm,
        message: `Run completed. Scanned=${data?.scanned ?? 0}, Synced=${data?.synced ?? 0}, Failed=${data?.failed ?? 0}`,
        messageType: "success",
      };
    } catch (err) {
      console.error("HubSpot scheduler run failed:", err);
      hubspotSchedulerForm = {
        ...hubspotSchedulerForm,
        message: "Failed to run scheduler now",
        messageType: "error",
      };
    } finally {
      hubspotSchedulerForm = { ...hubspotSchedulerForm, saving: false };
    }
  };

  const awsFields = [
    "awsAccessKeyId",
    "awsSecretAccessKey",
    "awsRegion",
    "awsServiceName",
    "awsCatalog",
  ];
  const hubspotFields = [
    "hubspotClientId",
    "hubspotClientSecret",
    "hubspotRedirectService",
    "hubspotRegService",
    "hubspotScopes",
    "hubspotAppId",
    "hubspotDeveloperApiKey",
    "hubspotWebhookUrl",
  ];
  const secretFields = [
    "awsAccessKeyId",
    "awsSecretAccessKey",
    "hubspotClientId",
    "hubspotClientSecret",
    "hubspotDeveloperApiKey",
    "aiApiKey",
  ];
  const additionalFields = ["additionalField1", "additionalField2"];
  const aiFields = ["aiProvider", "aiModel", "aiApiKey", "mcpServer"];
  const syncFields = ["hubspotSyncMode", "hubspotCustomerDataSource", "syncStages", "syncDuration", "alwaysFillAwsMandatoryFields", "defaultMandatoryFieldContext"];

  let config: Record<string, any> = {
    awsAccessKeyId: "",
    awsSecretAccessKey: "",
    awsRegion: "",
    awsServiceName: "",
    awsCatalog: "",
    awsOppIdName: "",
    hubspotClientId: "",
    hubspotClientSecret: "",
    hubspotRedirectService: "",
    hubspotRegService: "",
    hubspotScopes: "",
    hubspotAppId: "",
    hubspotDeveloperApiKey: "",
    hubspotWebhookUrl: "",
    additionalField1: "",
    additionalField2: "",
    aiProvider: "",
    aiModel: "",
    aiApiKey: "",
    mcpServer: process.env.SVELTE_APP_MCP_SERVER || "/mcp/acelink-mcp",
    hubspotSyncMode: "sync_yes_only",
    hubspotCustomerDataSource: "company_with_deal_override",
    awsStageAutoPropagation: false,
    awsStageMappings: "[]",
    syncStages: [],
    syncDuration: 12,
    alwaysFillAwsMandatoryFields: false,
    defaultMandatoryFieldContext: "",
  };

  let configId: number | null = null;
  let loadedAwsCatalog = "";
  let isLoading = false;
  let saveMessage = "";
  let saveMessageType: "success" | "error" = "success";
  let availableStages: Array<{ value: string; label: string; isSelected: boolean }> = [];
  let availableDurations: Array<{ value: number; label: string }> = [];
  let availableAiProviders: Array<{ value: string; label: string }> = [];
  let availableAiModels: Array<{ value: string; label: string }> = [];
  let loadingStages = false;
  let loadingDurations = false;
  let loadingAiProviders = false;
  let loadingAiModels = false;
  let aladvSettingsError = "";

  // Webhook state
  let webhookSubs: any[] = [];
  let webhookMsg = "";

  let hubspotTargetUrl: string | null = null;
  let webhookMismatch = false;
  let webhookWasAutoFilled = false;

  const visibilityMap: Record<string, boolean> = {};

  const maskSecret = (value: string): string => {
    if (!value) return "";
    const len = value.length;
    if (len <= 4) return "•".repeat(len);
    return value.slice(0, 2) + "•".repeat(len - 4) + value.slice(len - 2);
  };

  const toggleVisibility = (field: string) => {
    visibilityMap[field] = !visibilityMap[field];
  };

  const fetchAladvJson = async <T>(url: string, label: string): Promise<T | null> => {
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), DEFAULT_ALADV_REQUEST_TIMEOUT_MS);

    try {
      const res = await fetch(url, {
        headers: { accept: "application/json" },
        signal: controller.signal,
      });

      if (!res.ok) {
        throw new Error(`${label} request failed: ${res.status}`);
      }

      return (await res.json()) as T;
    } catch (err) {
      console.error(`Error loading ${label}:`, err);
      aladvSettingsError = ALADV_UNAVAILABLE_MESSAGE;
      return null;
    } finally {
      window.clearTimeout(timeoutId);
    }
  };

  const toBooleanSetting = (value: unknown): boolean =>
    value === true || value === 1 || String(value ?? "").toLowerCase() === "true";

  const fetchConfiguration = async () => {
    try {
      const res = await fetch(SETTINGS_URL);
      if (!res.ok) throw new Error("Failed to fetch configuration");
      const data = await res.json();
      configId = data?.id ?? null;
      const { id, ...rest } = data;
      config = {
        ...config,
        ...rest,
        awsCatalog: (data.awsCatalog || "") === "AWS" ? "prd" : "sandbox",
        hubspotSyncMode: data?.hubspotSyncMode || "sync_yes_only",
        hubspotCustomerDataSource: data?.hubspotCustomerDataSource || "company_with_deal_override",
        syncStages: data?.syncStages || [],
        syncDuration: data?.syncDuration || 12,
        mcpServer: data?.mcpServer || process.env.SVELTE_APP_MCP_SERVER || "/mcp/acelink-mcp",
        alwaysFillAwsMandatoryFields: toBooleanSetting(rest.alwaysFillAwsMandatoryFields),
        defaultMandatoryFieldContext: rest.defaultMandatoryFieldContext || ""
      };
      loadedAwsCatalog = config.awsCatalog;

      // --- also fetch HubSpot webhook target ---
      const hubspotRes = await fetch(`${WEBHOOK_BASE}/settings`);
      const hubspotData = await hubspotRes.json();

      if (hubspotRes.ok && hubspotData.success) {
        hubspotTargetUrl = hubspotData.settings?.targetUrl?.trim() || null;
        const savedUrl = config.hubspotWebhookUrl?.trim();

        if (!savedUrl && hubspotTargetUrl) {
          config.hubspotWebhookUrl = hubspotTargetUrl;
          webhookWasAutoFilled = true;
          saveMessage = "Webhook URL auto-filled from HubSpot.";
          saveMessageType = "success";
        }

        if (hubspotTargetUrl && savedUrl && savedUrl !== hubspotTargetUrl) {
          webhookMismatch = true;
        }
      }

      secretFields.forEach((field) => {
        visibilityMap[field] = !config[field] || config[field].trim() === "";
      });
    } catch (err) {
      console.error("Error loading configuration:", err);
    }
  };

  const saveConfiguration = async () => saveConfigurationInternal(false);

  const saveConfigurationInternal = async (skipCatalogConfirmation: boolean) => {
    isLoading = true;
    saveMessage = "";
    try {
      if (activeTab === "stageMapping") applyStageMappingToConfig();
      const nextAwsCatalog = config.awsCatalog === "prd" ? "prd" : "sandbox";
      const catalogChanged = !!loadedAwsCatalog && loadedAwsCatalog !== nextAwsCatalog;
      if (catalogChanged && !skipCatalogConfirmation) {
        confirmCatalogSwitch = true;
        return;
      }

      if (catalogChanged) {
        const catalogRes = await fetch(SAVE_SETTINGS_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ awsCatalog: nextAwsCatalog }),
        });
        if (!catalogRes.ok) throw new Error();
      }

      const method = configId ? "PUT" : "POST";
      const endpoint = configId ? `${API_URL}/${configId}` : API_URL;
      const res = await fetch(endpoint, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...config, awsCatalog: nextAwsCatalog }),
      });
      if (!res.ok) throw new Error();
      const data = res.status !== 204 ? await res.json() : null;
      if (!configId && data?.id) configId = data.id;
      loadedAwsCatalog = nextAwsCatalog;

      // Update HubSpot webhook URL if set
      if (config.hubspotWebhookUrl) {
        await fetch(`${WEBHOOK_BASE}/update-settings`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ webhookUrl: config.hubspotWebhookUrl }),
        });
      }

      saveMessage = activeTab === "stageMapping" ? "Stage mapping saved" : "Configuration saved";
      saveMessageType = "success";
      window.dispatchEvent(
        new CustomEvent("ai-config-updated", {
          detail: { mcpServer: config.mcpServer },
        })
      );
    } catch (err) {
      saveMessage = err instanceof Error ? err.message : "Failed to save configuration";
      saveMessageType = "error";
    } finally {
      isLoading = false;
    }
  };

  const confirmAndSaveCatalogSwitch = async () => {
    confirmCatalogSwitch = false;
    await saveConfigurationInternal(true);
  };

  const resetForm = async () => {
    try {
      Object.keys(config).forEach((key) => (config[key] = ""));
      config.alwaysFillAwsMandatoryFields = false;
      config.defaultMandatoryFieldContext = "";
      config.syncStages = [];
      config.syncDuration = 12;
      config.awsStageAutoPropagation = false;
      config.awsStageMappings = "[]";
      if (configId) {
        await fetch(`${API_URL}/${configId}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(config),
        });
      }

      const defaultsRes = await fetch(SETTINGS_URL);
      if (!defaultsRes.ok) throw new Error("Failed to fetch defaults");
      const defaultsData = await defaultsRes.json();
      configId = defaultsData?.id ?? configId;
      const { id, ...rest } = defaultsData;
      config = {
        ...config,
        ...rest,
        awsCatalog: (defaultsData.awsCatalog || "") === "AWS" ? "prd" : "sandbox",
        hubspotSyncMode: defaultsData?.hubspotSyncMode || "sync_yes_only",
        hubspotCustomerDataSource: defaultsData?.hubspotCustomerDataSource || "company_with_deal_override",
        syncStages: defaultsData?.syncStages || [],
        syncDuration: defaultsData?.syncDuration || 12,
        mcpServer: defaultsData?.mcpServer || process.env.SVELTE_APP_MCP_SERVER || "/mcp/acelink-mcp"
      };
      loadStageMapping();

      saveMessage = "Reset to default values";
      saveMessageType = "success";
      window.dispatchEvent(
        new CustomEvent("ai-config-updated", {
          detail: { mcpServer: config.mcpServer },
        })
      );
    } catch (err) {
      console.error("Reset failed:", err);
      saveMessage = "Failed to reset";
      saveMessageType = "error";
    }
  };

  const confirmAndReset = async () => {
    confirmReset = false;
    await resetForm();
  };

  const formatFieldLabel = (field: string): string => {
    if (field === "defaultMandatoryFieldContext") return "Default Fill Context";
    if (field === "mcpServer") return "MCP Server";
    if (field === "additionalField1") return "Additional Field 1";
    if (field === "additionalField2") return "Additional Field 2";

    return field
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/^aws/, "AWS")
      .replace(/^ai\b/i, "AI")
      .replace(/^hubspot/, "HubSpot")
      .replace(/^sync/, "Sync")
      .replace(/\bApi\b/g, "API")
      .replace(/id$/i, "ID")
      .replace(/url$/i, "URL")
      .replace(/api$/i, "API")
      .replace(/uri$/i, "URI");
  };

  const fetchAvailableStages = async () => {
    loadingStages = true;
    try {
      const data = await fetchAladvJson<any[]>(`${STAGES_URL}?filter=%7B%0A%0A%7D`, "stages");
      if (Array.isArray(data)) {
        availableStages = data.map((item) => ({
          value: item.name,
          label: item.name,
          isSelected: item?.isDefault === 1,
        }));
      }
    } finally {
      loadingStages = false;
    }
  };

  const fetchAvailableDurations = async () => {
    loadingDurations = true;
    try {
      const data = await fetchAladvJson<any[]>(
        `${DURATIONS_URL}?filter=%7B%0A%0A%7D`,
        "durations"
      );
      if (Array.isArray(data)) {
        availableDurations = data.map((item) => ({
          value: item.value,
          label: item.name,
        }));
      }
    } finally {
      loadingDurations = false;
    }
  };

  const fetchAvailableAiProviders = async () => {
    loadingAiProviders = true;
    try {
      const data = await fetchAladvJson<any[]>(AI_PROVIDERS_URL, "AI providers");
      if (Array.isArray(data)) {
        availableAiProviders = data
          .map((item) => ({
            value: item.value,
            label: item.name,
          }))
          .sort((a, b) => a.label.localeCompare(b.label));
      }
    } finally {
      loadingAiProviders = false;
    }
  };

  const fetchAvailableAiModels = async (provider = "") => {
    loadingAiModels = true;
    try {
      if (!provider) {
        availableAiModels = [];
        return;
      }

      const res = await fetch(AI_MODELS_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ provider }),
      });
      if (!res.ok) throw new Error(`Failed to fetch AI models (${res.status})`);

      const data = await res.json();
      const models = data.models || [];
      if (Array.isArray(models)) {
        availableAiModels = models
          .map((item) => ({
            value: item.value,
            label: item.label,
          }))
          .sort((a, b) => a.label.localeCompare(b.label));
      } else {
        availableAiModels = [];
      }
    } finally {
      loadingAiModels = false;
    }
  };

  function toggleStage(stageValue: string) {
    if (!Array.isArray(config.syncStages)) {
      config.syncStages = [];
    }

    const index = config.syncStages.indexOf(stageValue);
    if (index > -1) {
      config.syncStages.splice(index, 1);
      config.syncStages = config.syncStages;
    } else {
      config.syncStages.push(stageValue);
      config.syncStages = config.syncStages;
    }
  }

  function isStageSelected(stageValue: string): boolean {
    if (!Array.isArray(config.syncStages)) return false;
    return config.syncStages.includes(stageValue);
  }

  const handleAiProviderChange = async (event: Event) => {
    const nextProvider = (event.currentTarget as HTMLSelectElement).value;
    config.aiProvider = nextProvider;
    config.aiModel = "";
    await fetchAvailableAiModels(nextProvider);
  };

  $: selectedStagesCount = Array.isArray(config.syncStages) ? config.syncStages.length : 0;
  $: configuredStageMappingsCount = stageMappings.filter(
    (row) => row.pipelineId && row.hubspotStageId
  ).length;

  const setActiveTab = (tab: SettingsTab): void => {
    if (tab !== activeTab) saveMessage = "";
    activeTab = tab;
    if (tab === "bulksync") {
      fetchBulkSyncConfig("partner");
      fetchBulkSyncConfig("aws");
    } else if (tab === "hubspotScheduler") {
      fetchHubspotSchedulerConfig();
    } else if (tab === "cleanup") {
      fetchBulkSyncConfig("cleanup");
    } else if (tab === "stageMapping" && isHubspotAuthenticated && hubspotPipelines.length === 0 && !stageMappingsLoading) {
      fetchHubspotPipelines();
    }
  };

  const setSettingsNavCollapsed = (collapsed: boolean): void => {
    isSettingsNavCollapsed = collapsed;
    localStorage.setItem(SETTINGS_NAV_COLLAPSED_KEY, String(collapsed));
  };

  // --- Webhook functions ---
  const fetchSubscriptions = async () => {
    try {
      const res = await fetch(`${WEBHOOK_BASE}/deal-subscriptions`);
      const data = await res.json();
      if (data.success) {
        webhookSubs = data.subscriptions ?? [];
        webhookMsg = "Fetched webhook subscriptions.";
      } else {
        webhookMsg = data.error;
      }
    } catch {
      webhookMsg = "Failed to fetch subscriptions.";
    }
  };

  const activateSubscriptions = async () => {
    try {
      const res = await fetch(`${WEBHOOK_BASE}/activate-deal-subscriptions`, {
        method: "POST",
      });
      const data = await res.json();
      webhookMsg = data.success
        ? `Activated subscriptions. Created=${data.created?.length}, Skipped=${data.skipped?.length}`
        : data.error;
      await fetchSubscriptions();
    } catch {
      webhookMsg = "Failed to activate subscriptions.";
    }
  };

  const deleteSubscriptions = async () => {
    try {
      const res = await fetch(`${WEBHOOK_BASE}/delete-deal-subscriptions`, {
        method: "POST",
      });
      const data = await res.json();
      webhookMsg = data.success
        ? `Deleted ${data.deleted?.length} subscriptions.`
        : data.error;
      webhookSubs = [];
    } catch {
      webhookMsg = "Failed to delete subscriptions.";
    }
  };

  const initializeSettings = async () => {
    await fetchConfiguration();
    loadStageMapping();
    aladvSettingsError = "";
    await Promise.all([
      fetchAvailableStages(),
      fetchAvailableDurations(),
      fetchAvailableAiProviders(),
      fetchAvailableAiModels(config.aiProvider || ""),
      refreshHubspotAuthStatus(),
      fetchSubscriptions(),
      fetchBulkSyncConfig("partner"),
      fetchBulkSyncConfig("aws"),
      fetchHubspotSchedulerConfig(),
      fetchBulkSyncConfig("cleanup"),
    ]);

    if (!config.syncStages || config.syncStages.length === 0) {
      config.syncStages = availableStages
        .filter((stage) => stage.isSelected)
        .map((stage) => stage.value);
    }
  };

  onMount(() => {
    isSettingsNavCollapsed = localStorage.getItem(SETTINGS_NAV_COLLAPSED_KEY) === "true";
    window.addEventListener(HUBSPOT_AUTH_CHANGED_EVENT, refreshHubspotAuthStatus);
    void initializeSettings();
  });

  onDestroy(() => {
    window.removeEventListener(HUBSPOT_AUTH_CHANGED_EVENT, refreshHubspotAuthStatus);
  });
</script>

<div class="mx-auto w-full">
  <h2 class="mb-4 mt-2 max-w-full break-words text-lg font-bold leading-tight text-white sm:mb-5 sm:mt-0 sm:text-2xl">
    AcelinkHS Configurations
  </h2>

  <div class:collapsed-settings-nav={isSettingsNavCollapsed} class="settings-shell">
    <aside class="flex flex-col justify-between border-r border-secondary-700 bg-secondary-800 p-2 md:p-3">
      <nav class="flex flex-col gap-1" aria-label="Settings sections">
        {#each visibleSettingsSections as section}
          <button
            type="button"
            class={`settings-nav-item flex items-center gap-3 rounded-lg px-3 py-3 text-left transition-colors md:px-4 ${
              activeTab === section.id
                ? "bg-primary-600 text-white"
                : "text-secondary-300 hover:bg-secondary-700 hover:text-white"
            }`}
            on:click={() => setActiveTab(section.id)}
            title={section.label}
          >
            {#if isSettingsNavCollapsed}
              <span class="flex justify-center">
                <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d={section.iconPath} stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              </span>
            {:else}
              <span class="flex items-center gap-2 md:hidden">
                <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d={section.iconPath} stroke-linecap="round" stroke-linejoin="round" />
                </svg>
                <span class="settings-nav-label text-sm font-semibold">{section.label}</span>
              </span>
              <span class="settings-nav-copy hidden items-start gap-3 md:flex">
                <svg class="mt-0.5 h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d={section.iconPath} stroke-linecap="round" stroke-linejoin="round" />
                </svg>
                <span>
                  <span class="block text-sm font-semibold">{section.label}</span>
                  <span class="settings-nav-description mt-0.5 block text-xs text-secondary-400">{section.description}</span>
                </span>
              </span>
            {/if}
          </button>
        {/each}
      </nav>

      <button
        type="button"
        class="settings-bottom-toggle mt-4 flex h-10 w-10 items-center justify-center rounded-lg border border-secondary-600 bg-secondary-800 text-secondary-300 hover:border-primary-500 hover:bg-secondary-700 hover:text-white"
        on:click={() => setSettingsNavCollapsed(!isSettingsNavCollapsed)}
        title={isSettingsNavCollapsed ? "Expand navigation" : "Collapse navigation"}
      >
        <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          {#if isSettingsNavCollapsed}
            <path d="M9 18l6-6-6-6" stroke-linecap="round" stroke-linejoin="round" />
          {:else}
            <path d="M15 18l-6-6 6-6" stroke-linecap="round" stroke-linejoin="round" />
          {/if}
        </svg>
      </button>
    </aside>

  <form on:submit|preventDefault={saveConfiguration} class="settings-content space-y-5 p-4 sm:p-6 lg:p-7">
    <div class="border-b border-secondary-800 pb-4">
      <h3 class="text-lg font-semibold text-white">{activeSettingsSection?.label}</h3>
      <p class="mt-1 text-sm text-secondary-400">{activeSettingsSection?.description}</p>
    </div>
    {#if aladvSettingsError && (activeTab === "sync" || activeTab === "ai")}
      <div class="p-3 rounded bg-red-100 text-red-700 text-sm">
        {aladvSettingsError}
      </div>
    {/if}

    {#if activeTab !== "bulksync" && activeTab !== "hubspotScheduler" && activeTab !== "cleanup" && activeTab !== "hubspotCard"}
      <div class="grid grid-cols-1 gap-x-8 gap-y-5 lg:grid-cols-2">
      {#each Object.entries(config) as [field, value] (field)}
        {#if (activeTab === "aws" && awsFields.includes(field)) || (activeTab === "hubspot" && hubspotFields.includes(field)) || (activeTab === "additional" && additionalFields.includes(field)) || (activeTab === "ai" && aiFields.includes(field)) || (activeTab === "sync" && syncFields.includes(field))}
        {#if activeTab === "sync" && field === "alwaysFillAwsMandatoryFields"}
          <div class="lg:col-span-2 pt-3 border-t border-secondary-800">
            <h4 class="text-sm font-semibold text-white">AWS mandatory field fill</h4>
            <p class="mt-1 text-xs text-secondary-400">AI assistance for AWS Partner Central mandatory fields.</p>
          </div>
        {/if}
        <div class={wideSettingsFields.has(field) ? "lg:col-span-2" : ""}>
          {#if field !== "alwaysFillAwsMandatoryFields"}
            <label
              class="block text-sm font-medium text-secondary-300 mb-1"
              for={field}
            >
              {formatFieldLabel(field)}
            </label>
          {/if}
          <div>
          {#if field === "awsCatalog"}
              <select
                id="awsCatalog"
                bind:value={config.awsCatalog}
                class="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-blue-500"
              >
                <option value="sandbox">Sandbox</option>
                <option value="prd">AWS</option>
              </select>
          {:else if secretFields.includes(field)}
              <div class="relative">
                {#if visibilityMap[field]}
                  <input
                    id={field}
                    type="text"
                    class="w-full border rounded px-3 pr-10 py-2 focus:ring-2 focus:ring-blue-500"
                    bind:value={config[field]}
                    placeholder={`Enter ${formatFieldLabel(field)}`}
                  />
                {:else}
                  <input
                    id={field}
                    type="text"
                    class="w-full border rounded px-3 pr-10 py-2 text-secondary-400 bg-secondary-900 focus:ring-2 focus:ring-blue-500"
                    value={maskSecret(config[field])}
                    readonly
                    placeholder={`Enter ${formatFieldLabel(field)}`}
                  />
                {/if}
                <button
                  type="button"
                  class="absolute inset-y-0 right-2 flex items-center text-secondary-500 hover:text-secondary-400 focus:outline-none"
                  on:click={() => toggleVisibility(field)}
                >
                  {#if visibilityMap[field]}
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-5 w-5 sm:h-6 sm:w-6"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M13.875 18.825A10.05 10.05 0 0112 19c-5.523 0-10-5-10-7a10.05 10.05 0 012.458-3.825m3.877-2.672A9.956 9.956 0 0112 5c4.477 0 8.268 2.943 9.542 7a9.956 9.956 0 01-4.132 5.411M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M3 3l18 18"
                      />
                    </svg>
                  {:else}
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-5 w-5 sm:h-6 sm:w-6"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                      />
                    </svg>
                  {/if}
                </button>
              </div>
            {:else if field === "hubspotSyncMode"}
              <select
                id={field}
                bind:value={config[field]}
                class="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-blue-500"
              >
                <option value="sync_yes_only">Only Sync to AWS = Yes</option>
                <option value="sync_all">Sync all HubSpot records</option>
                <option value="sync_all_except_no">Sync all except Sync to AWS = No</option>
              </select>
              <p class="mt-1 text-xs text-secondary-400">
                Controls how HubSpot-originated deals are handled: only explicit Yes, all records, or all except records explicitly set to No.
              </p>
            {:else if field === "hubspotCustomerDataSource"}
              <select
                id={field}
                bind:value={config[field]}
                class="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-blue-500"
              >
                <option value="company_with_deal_override">Company with deal override</option>
                <option value="deal_only">Deal fields only</option>
              </select>
              <p class="mt-1 text-xs text-secondary-400">
                Controls whether Acelink customer fields use the linked HubSpot company by default or only the deal fields.
              </p>
            {:else if field === "syncStages"}
              <div class="border border-secondary-600 rounded-lg overflow-hidden">
                <div class="bg-secondary-900 px-3 py-2 border-b border-secondary-600">
                  <span class="text-sm font-medium text-secondary-300">
                    {loadingStages ? "Loading..." : `${selectedStagesCount} of ${availableStages.length} selected`}
                  </span>
                </div>
                <div class="max-h-48 overflow-y-auto">
                  {#if loadingStages}
                    <div class="px-3 py-4 text-center text-secondary-400">
                      <span>Loading stages...</span>
                    </div>
                  {:else if availableStages.length === 0}
                    <div class="px-3 py-4 text-center text-secondary-400">
                      <span>No stages available</span>
                    </div>
                  {:else}
                    {#each availableStages as stage}
                      <label class="flex items-center px-3 py-2.5 hover:bg-secondary-700 cursor-pointer border-b border-secondary-800 last:border-b-0 transition-colors">
                        <input
                          type="checkbox"
                          value={stage.value}
                          checked={isStageSelected(stage.value)}
                          on:change={() => toggleStage(stage.value)}
                          class="w-4 h-4 text-blue-600 border-secondary-600 rounded focus:ring-2 focus:ring-blue-500 focus:ring-offset-0"
                        />
                        <span class="ml-3 text-sm text-secondary-300 select-none">{stage.label}</span>
                      </label>
                    {/each}
                  {/if}
                </div>
              </div>
              <p class="mt-1 text-xs text-secondary-400">
                Select stages to sync from AWS during sync operations
              </p>
            {:else if field === "syncDuration"}
              <select
                id={field}
                bind:value={config[field]}
                class="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-blue-500"
                disabled={loadingDurations}
              >
                {#if loadingDurations}
                  <option value="">Loading durations...</option>
                {:else if availableDurations.length === 0}
                  <option value="">No durations available</option>
                {:else}
                  {#each availableDurations as duration}
                    <option value={duration.value}>{duration.label}</option>
                  {/each}
                {/if}
              </select>
              <p class="mt-1 text-xs text-secondary-400">
                Sync opportunities modified within the selected duration
              </p>
            {:else if field === "alwaysFillAwsMandatoryFields"}
              <div class="rounded-lg border border-secondary-700 bg-secondary-900/40 px-4 py-4">
                <label class="flex items-start gap-3 cursor-pointer">
                  <input
                    id={field}
                    type="checkbox"
                    bind:checked={config[field]}
                    class="mt-1 w-4 h-4 text-blue-600 border-secondary-600 rounded focus:ring-2 focus:ring-blue-500 focus:ring-offset-0"
                  />
                  <span>
                    <span class="block text-sm font-semibold text-white">Auto-fill AWS mandatory fields</span>
                    <span class="block mt-1 text-sm text-secondary-400">
                      After a HubSpot opportunity update, use AI to fill missing AWS mandatory fields before syncing.
                    </span>
                  </span>
                </label>
              </div>
            {:else if field === "defaultMandatoryFieldContext"}
              <textarea
                id={field}
                bind:value={config[field]}
                rows="4"
                maxlength="5000"
                placeholder="e.g. If no country is defined, use Switzerland."
                class="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-blue-500"
              ></textarea>
              <p class="mt-1 text-sm text-gray-600">
                Applied as default context to every AWS mandatory field fill. Specific opportunity data always takes precedence.
              </p>
            {:else if field === "aiProvider"}
              <select
                id={field}
                value={config[field]}
                on:change={handleAiProviderChange}
                class="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-blue-500"
                disabled={loadingAiProviders}
              >
                {#if loadingAiProviders}
                  <option value="">Loading AI providers...</option>
                {:else if availableAiProviders.length === 0}
                  <option value="">No AI providers available</option>
                {:else}
                  <option value="">Select AI provider</option>
                  {#each availableAiProviders as provider}
                    <option value={provider.value}>{provider.label}</option>
                  {/each}
                {/if}
              </select>
            {:else if field === "aiModel"}
              <select
                id={field}
                bind:value={config[field]}
                class="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-blue-500"
                disabled={loadingAiModels || !config.aiProvider}
              >
                {#if loadingAiModels}
                  <option value="">Loading AI models...</option>
                {:else if !config.aiProvider}
                  <option value="">Select AI provider first</option>
                {:else if availableAiModels.length === 0}
                  <option value="">No AI models available</option>
                {:else}
                  <option value="">Select AI model</option>
                  {#each availableAiModels as model}
                    <option value={model.value}>{model.label}</option>
                  {/each}
                {/if}
              </select>
            {:else if field === "mcpServer"}
              <select
                id={field}
                bind:value={config[field]}
                class="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-blue-500"
              >
                <option value="/mcp/acelink-mcp">Acelink</option>
                <option value="/alhs/alace/mcp/mcp-aws-apn">AWS APN</option>
                <option value="/mcp/acelink-plus-aws-apn">Acelink + AWS APN</option>
              </select>
            {:else}
              <input
                id={field}
                type="text"
                class="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-blue-500"
                bind:value={config[field]}
                placeholder={`Enter ${formatFieldLabel(field)}`}
              />
            {/if}
            {#if field === "aiApiKey"}
              <p class="mt-1 text-xs text-secondary-400">
                Paste the API key of the AI service you want to use. This is saved here.
              </p>
            {:else if field === "aiProvider"}
              <p class="mt-1 text-xs text-secondary-400">
                Choose the AI service you want to use.
              </p>
            {:else if field === "aiModel"}
              <p class="mt-1 text-xs text-secondary-400">
                Choose the model for the selected AI service.
              </p>
            {:else if field === "mcpServer"}
              <p class="mt-1 text-xs text-secondary-400">
                Select the MCP Server configuration.
              </p>
            {/if}
          </div>
        </div>
        {/if}
      {/each}
      </div>
    {/if}

    {#if activeTab === "stageMapping"}
      <section class="space-y-5">
        {#if hubspotAuthChecked && !isHubspotAuthenticated}
          <div class="rounded-lg border border-orange-500/40 bg-orange-500/10 p-4">
            <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p class="text-sm font-semibold text-orange-200">HubSpot authentication required</p>
                <p class="mt-1 text-sm text-orange-300">Sign in to HubSpot to configure deal stage mappings.</p>
              </div>
              <Button variant="warning" on:click={connectHubspot}>
                Connect HubSpot
              </Button>
            </div>
          </div>
        {/if}
        {#if !hubspotAuthChecked}
          <p class="text-sm text-secondary-400">Checking HubSpot authentication...</p>
        {:else if isHubspotAuthenticated}
        <div class="rounded-xl border border-secondary-700 bg-secondary-900/40 p-4">
          <div class="flex items-center justify-between gap-4">
            <div>
              <h4 class="text-sm font-semibold text-white">Automatic stage propagation</h4>
              <p class="mt-1 text-xs text-secondary-400">
                Propagate HubSpot Deal Stage and AWS Stage changes in both directions.
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={autoPropagateStages}
              class={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent ${autoPropagateStages ? "bg-primary-600" : "bg-secondary-600"}`}
              on:click={() => {
                autoPropagateStages = !autoPropagateStages;
                stageMappingsError = "";
              }}
            >
              <span class={`inline-block h-5 w-5 transform rounded-full bg-white shadow ${autoPropagateStages ? "translate-x-5" : "translate-x-0"}`} />
            </button>
          </div>
        </div>

        <div class="overflow-hidden rounded-xl border border-secondary-700">
          <div class="grid grid-cols-[minmax(0,1fr)_minmax(0,1.5fr)] gap-4 bg-secondary-800 px-4 py-3 text-xs font-semibold uppercase text-secondary-400">
            <span>AWS Sales Stage</span>
            <span>HubSpot Deal Stage</span>
          </div>
          {#each stageMappings as row (row.awsStage)}
            <div class="grid grid-cols-1 gap-2 border-t border-secondary-700 px-4 py-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1.5fr)] md:items-center md:gap-4">
              <span class="text-sm font-medium text-secondary-200">{row.awsStage}</span>
              <select
                value={stageSelectionValue(row)}
                disabled={stageMappingsLoading}
                class="w-full rounded border border-secondary-600 bg-secondary-900 px-3 py-2 text-sm text-white"
                on:change={(event) => setStageSelection(row.awsStage, event.currentTarget.value)}
              >
                <option value="">-- Select HubSpot deal stage --</option>
                {#each hubspotPipelines as pipeline}
                  <optgroup label={pipeline.label}>
                    {#each pipeline.stages as stage}
                      <option value={`${pipeline.id}::${stage.id}`}>{stage.label}</option>
                    {/each}
                  </optgroup>
                {/each}
              </select>
            </div>
          {/each}
        </div>

        <div class="grid grid-cols-1 gap-2 sm:flex sm:justify-end">
          <Button
            on:click={fillRecommendedStageMapping}
            disabled={stageMappingsLoading || hubspotPipelines.length === 0}
          >
            Fill recommended mapping
          </Button>
          <Button
            variant="secondary"
            on:click={fetchHubspotPipelines}
            disabled={stageMappingsLoading || !isHubspotAuthenticated}
          >
            {stageMappingsLoading ? "Refreshing..." : "Refresh"}
          </Button>
        </div>
        {#if stageMappingsError}
          <div class="rounded border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-200">{stageMappingsError}</div>
        {/if}
        {#if pipelinesError}
          <div class="rounded border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-200">{pipelinesError}</div>
        {/if}
        {:else if configuredStageMappingsCount > 0}
          <div class="rounded-lg border border-secondary-700 bg-secondary-900/40 p-4">
            <p class="text-sm font-semibold text-secondary-200">Saved stage mapping</p>
            <p class="mt-1 text-sm text-secondary-400">
              {configuredStageMappingsCount} of {AWS_SALES_STAGES.length} mappings are saved. Connect HubSpot to view or edit them.
            </p>
          </div>
        {/if}
        {#if stageMappingsError && !isHubspotAuthenticated}
          <div class="rounded border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-200">{stageMappingsError}</div>
        {/if}
      </section>
    {/if}

    <!-- Webhook Management -->
    {#if activeTab === "hubspot"}
      <div class="mt-8 border-t pt-6 space-y-4">
        <h3 class="text-lg font-semibold text-secondary-800">
          Webhook Management
        </h3>

        <!-- Actions -->
        <div class="flex flex-wrap gap-3">
          <Button on:click={activateSubscriptions}>✅ Activate Defaults</Button>
          <Button variant="danger" on:click={deleteSubscriptions}>🗑 Delete All</Button>
          <Button variant="secondary" on:click={fetchSubscriptions}>🔄 Refresh</Button>
        </div>

        <!-- Subscription List -->
        {#if webhookSubs.length > 0}
          <ul class="space-y-2 text-sm">
            {#each webhookSubs as sub}
              <li
                class="p-3 border rounded-lg bg-secondary-50 shadow-sm flex items-center justify-between animate-fade-in"
              >
                <div>
                  <strong class="text-secondary-800">{sub.eventType}</strong>
                  {#if sub.propertyName}
                    <span class="ml-1 text-secondary-600"
                      >({sub.propertyName})</span
                    >
                  {/if}
                  <span class="ml-2 text-secondary-400 text-xs"
                    >ID: {sub.id}</span
                  >
                </div>
                <div>
                  {#if sub.active}
                    <span
                      class="px-2 py-0.5 text-xs font-medium rounded-full bg-success-100 text-success-700"
                    >
                      Active
                    </span>
                  {:else}
                    <span
                      class="px-2 py-0.5 text-xs font-medium rounded-full bg-error-100 text-error-700"
                    >
                      Inactive
                    </span>
                  {/if}
                </div>
              </li>
            {/each}
          </ul>
        {:else}
          <p class="text-sm text-secondary-500">No deal subscriptions found.</p>
        {/if}

        <!-- Status Message -->
        {#if webhookMsg}
          <div
            class="p-2 text-xs rounded bg-secondary-50 text-secondary-700 shadow animate-fade-in"
          >
            {webhookMsg}
          </div>
        {/if}
      </div>
    {/if}

    {#if activeTab === "hubspot"}
      {#if webhookMismatch}
        <div class="p-2 rounded bg-yellow-100 text-yellow-800 text-sm">
          Warning: Saved webhook URL does not match HubSpot's current target URL
          ({hubspotTargetUrl}).
        </div>
      {/if}
    {/if}

    {#if activeTab === "hubspotCard"}
      <div class="space-y-4">

        <div>
          <label for="card-credential" class="block text-sm font-medium text-secondary-300 mb-1">
            Personal Access Key / Bearer Token
          </label>
          <input
            id="card-credential"
            type="text"
            bind:value={cardSetupCredential}
            placeholder="Paste HubSpot Personal Access Key"
            class="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-blue-500"
          />
          <p class="text-xs text-secondary-400 mt-1">
            Use a HubSpot Personal Access Key from <code>Development → Keys → Personal Access Key</code>,
            or a bearer token if you explicitly have one. Requires the <code>developer.projects.write</code> scope.
          </p>
        </div>

        <div>
          <label for="card-portal-id" class="block text-sm font-medium text-secondary-300 mb-1">
            Portal ID <span class="text-red-500">*</span>
          </label>
          <input
            id="card-portal-id"
            type="text"
            bind:value={cardSetupPortalId}
            placeholder="e.g. 12345678"
            class="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-blue-500"
          />
          <p class="text-xs text-secondary-400 mt-1">
            Your HubSpot developer portal ID. Found in the URL when logged in:
            <code>app.hubspot.com/settings/<strong>12345678</strong>/...</code>
          </p>
        </div>

        <div>
          <label for="card-project-name" class="block text-sm font-medium text-secondary-300 mb-1">
            Project Name
          </label>
          <input
            id="card-project-name"
            type="text"
            bind:value={cardSetupProjectName}
            placeholder={DEFAULT_PROJECT_NAME}
            class="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-blue-500"
          />
          <p class="text-xs text-secondary-400 mt-1">
            Used as the HubSpot project identifier. Change this per customer if needed.
          </p>
        </div>

        <div>
          <label for="card-domain-url" class="block text-sm font-medium text-secondary-300 mb-1">
            Card Base URL
          </label>
          <input
            id="card-domain-url"
            type="text"
            bind:value={cardSetupDomainUrl}
            placeholder="https://your-public-host"
            class="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-blue-500"
          />
          <p class="text-xs text-secondary-400 mt-1">
            Enter the public backend (grapi) URL.
          </p>
        </div>

        <div class="flex flex-wrap gap-3">
          <Button disabled={cardDeployLoading} on:click={deployCard}>
            {cardDeployLoading ? "Preparing install… (Please wait)" : "Upload and Open Install"}
          </Button>
          <Button variant="secondary" disabled={cardInstallLoading || !cardProjectUploaded} on:click={openInstallPage}>
            {cardInstallLoading ? "Opening…" : "Open Install Page →"}
          </Button>
        </div>

        {#if !cardProjectUploaded}
          <p class="text-xs text-secondary-500">
            One click uploads the project and opens the HubSpot install flow in a new window.
          </p>
        {/if}

        {#if cardSetupError}
          <p class="text-sm text-red-600">{cardSetupError}</p>
        {/if}

        {#if cardSetupSuccess}
          <p class="text-sm text-green-700">{cardSetupSuccess}</p>
        {/if}

        {#if cardSetupInstallUrl}
          <p class="text-sm text-secondary-600">
            Finish the HubSpot install flow in the opened page so the card can appear on the portal.
            <a href={cardSetupInstallUrl} target="_blank" class="text-blue-600 underline ml-1">
              Open again →
            </a>
          </p>
        {/if}

      </div>
    {/if}

    {#if activeTab === "upgrade"}
      <UpgradeManager />
    {/if}

    {#if activeTab === "bulksync"}
      <div class="flex flex-col gap-4">
        <div class="overflow-hidden">
          {#if !bulkSyncForm.synced}
            <div class="px-4 sm:px-6 py-3 bg-yellow-50 border-b border-yellow-300 rounded-t-lg">
              <div class="flex flex-wrap items-center justify-between gap-2">
                <div class="flex items-center gap-2 text-yellow-900">
                  <svg class="w-4 h-4 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                  </svg>
                  <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800">
                    Config out of sync
                  </span>
                  <span class="text-xs text-amber-300">
                    Running: <code class="bg-yellow-100 px-1 rounded">{bulkSyncForm.runningSchedule || 'not set'}</code>
                    ({bulkSyncForm.runningEnabled ? 'enabled' : 'disabled'})
                  </span>
                </div>
                <span class="text-xs text-amber-300">Save to apply changes.</span>
              </div>
            </div>
          {/if}

          <div class="px-4 sm:px-6 py-4 sm:py-5 border-b border-secondary-800">
            <div class="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
              <div class="flex items-center gap-3">
                <div class="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                  <svg class="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </div>
                <div>
                  <h4 class="text-base sm:text-lg font-semibold text-white">Bulk Sync From HubSpot</h4>
                  <p class="text-sm text-secondary-400">Sync opportunities from HubSpot to shared database</p>
                </div>
              </div>

              <div class="flex items-center gap-3 self-end sm:self-auto">
                <span class="text-sm text-secondary-400">{bulkSyncForm.enabled ? 'Enabled' : 'Disabled'}</span>
                <button
                  type="button"
                  class={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${bulkSyncForm.enabled ? 'bg-primary-600' : 'bg-secondary-600'}`}
                  on:click={() => (bulkSyncForm = { ...bulkSyncForm, enabled: !bulkSyncForm.enabled })}
                >
                  <span class="sr-only">Enable sync</span>
                  <span
                    class={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-secondary-800 shadow ring-0 transition duration-200 ease-in-out ${bulkSyncForm.enabled ? 'translate-x-5' : 'translate-x-0'}`}
                  ></span>
                </button>
              </div>
            </div>
          </div>

          {#if !bulkSyncForm.loading}
          <div class="px-4 sm:px-6 py-4 space-y-4">
            <div>
              <div class="block text-sm font-medium text-secondary-300 mb-2">
                <div class="flex items-center gap-2">
                  <svg class="w-4 h-4 text-secondary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Schedule (Cron)
                </div>
              </div>
              <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 sm:gap-3">
                <div>
                  <label for="bulk-sync-minute" class="block text-xs text-secondary-400 mb-1">Minute</label>
                  <input type="text" id="bulk-sync-minute" bind:value={bulkSyncForm.minute} placeholder="*" class="w-full border border-secondary-700 rounded-lg px-3 py-2.5 text-sm text-white placeholder-secondary-500 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors" />
                </div>
                <div>
                  <label for="bulk-sync-hour" class="block text-xs text-secondary-400 mb-1">Hour</label>
                  <input type="text" id="bulk-sync-hour" bind:value={bulkSyncForm.hour} placeholder="*" class="w-full border border-secondary-700 rounded-lg px-3 py-2.5 text-sm text-white placeholder-secondary-500 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors" />
                </div>
                <div>
                  <label for="bulk-sync-day" class="block text-xs text-secondary-400 mb-1">Day</label>
                  <input type="text" id="bulk-sync-day" bind:value={bulkSyncForm.dayOfMonth} placeholder="*" class="w-full border border-secondary-700 rounded-lg px-3 py-2.5 text-sm text-white placeholder-secondary-500 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors" />
                </div>
                <div>
                  <label for="bulk-sync-month" class="block text-xs text-secondary-400 mb-1">Month</label>
                  <input type="text" id="bulk-sync-month" bind:value={bulkSyncForm.month} placeholder="*" class="w-full border border-secondary-700 rounded-lg px-3 py-2.5 text-sm text-white placeholder-secondary-500 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors" />
                </div>
                <div>
                  <label for="bulk-sync-weekday" class="block text-xs text-secondary-400 mb-1">Weekday</label>
                  <input type="text" id="bulk-sync-weekday" bind:value={bulkSyncForm.dayOfWeek} placeholder="*" class="w-full border border-secondary-700 rounded-lg px-3 py-2.5 text-sm text-white placeholder-secondary-500 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors" />
                </div>
              </div>
            </div>

            <div>
              <label for="bulk-sync-timezone" class="block text-sm font-medium text-secondary-300 mb-2">
                <div class="flex items-center gap-2">
                  <svg class="w-4 h-4 text-secondary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Timezone
                </div>
              </label>
              <input type="text" id="bulk-sync-timezone" bind:value={bulkSyncForm.timezone} placeholder="UTC" class="w-full sm:max-w-xs border border-secondary-700 rounded-lg px-3 py-2.5 text-sm text-white placeholder-secondary-500 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors" />
              <p class="text-xs text-secondary-500 mt-2">e.g. UTC, America/New_York</p>
            </div>
          </div>

          <hr class="border-t border-secondary-800" />
          <div class="px-4 sm:px-6 py-4 space-y-3">
            {#if bulkSyncForm.message}
              <div class={`flex items-center gap-2 text-sm ${bulkSyncForm.messageType === "success" ? "text-green-600" : "text-red-600"}`}>
                {#if bulkSyncForm.messageType === "success"}
                  <svg class="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                  </svg>
                {:else}
                  <svg class="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                  </svg>
                {/if}
                <span>{bulkSyncForm.message}</span>
              </div>
            {/if}
          </div>
          {/if}
        </div>

        <div class="overflow-hidden">
          {#if !awsBulkSyncForm.synced}
            <div class="px-4 sm:px-6 py-3 bg-yellow-50 border-b border-yellow-300 rounded-t-lg">
              <div class="flex flex-wrap items-center justify-between gap-2">
                <div class="flex items-center gap-2 text-yellow-900">
                  <svg class="w-4 h-4 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                  </svg>
                  <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800">
                    Config out of sync
                  </span>
                  <span class="text-xs text-amber-300">
                    Running: <code class="bg-yellow-100 px-1 rounded">{awsBulkSyncForm.runningSchedule || 'not set'}</code>
                    ({awsBulkSyncForm.runningEnabled ? 'enabled' : 'disabled'})
                  </span>
                </div>
                <span class="text-xs text-amber-300">Save to apply changes.</span>
              </div>
            </div>
          {/if}

          <div class="px-4 sm:px-6 py-4 sm:py-5 border-b border-secondary-800">
            <div class="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
              <div class="flex items-center gap-3">
                <div class="w-10 h-10 rounded-lg bg-secondary-700 flex items-center justify-center flex-shrink-0">
                  <svg class="w-5 h-5 text-secondary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </div>
                <div>
                  <h4 class="text-base sm:text-lg font-semibold text-white">Bulk Sync To AWS</h4>
                  <p class="text-sm text-secondary-400">Sync opportunities from shared database to AWS</p>
                </div>
              </div>

              <div class="flex items-center gap-3 self-end sm:self-auto">
                <span class="text-sm text-secondary-400">{awsBulkSyncForm.enabled ? 'Enabled' : 'Disabled'}</span>
                <button
                  type="button"
                  class={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:ring-offset-2 ${awsBulkSyncForm.enabled ? 'bg-secondary-600' : 'bg-secondary-600'}`}
                  on:click={() => (awsBulkSyncForm = { ...awsBulkSyncForm, enabled: !awsBulkSyncForm.enabled })}
                >
                  <span class="sr-only">Enable sync</span>
                  <span
                    class={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-secondary-800 shadow ring-0 transition duration-200 ease-in-out ${awsBulkSyncForm.enabled ? 'translate-x-5' : 'translate-x-0'}`}
                  ></span>
                </button>
              </div>
            </div>
          </div>

          {#if !awsBulkSyncForm.loading}
          <div class="px-4 sm:px-6 py-4 space-y-4">
            <div>
              <div class="block text-sm font-medium text-secondary-300 mb-2">
                <div class="flex items-center gap-2">
                  <svg class="w-4 h-4 text-secondary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Schedule (Cron)
                </div>
              </div>
              <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 sm:gap-3">
                <div>
                  <label for="bulk-sync-aws-minute" class="block text-xs text-secondary-400 mb-1">Minute</label>
                  <input type="text" id="bulk-sync-aws-minute" bind:value={awsBulkSyncForm.minute} placeholder="*" class="w-full border border-secondary-700 rounded-lg px-3 py-2.5 text-sm text-white placeholder-secondary-500 focus:ring-2 focus:ring-secondary-500 focus:border-secondary-500 transition-colors" />
                </div>
                <div>
                  <label for="bulk-sync-aws-hour" class="block text-xs text-secondary-400 mb-1">Hour</label>
                  <input type="text" id="bulk-sync-aws-hour" bind:value={awsBulkSyncForm.hour} placeholder="*" class="w-full border border-secondary-700 rounded-lg px-3 py-2.5 text-sm text-white placeholder-secondary-500 focus:ring-2 focus:ring-secondary-500 focus:border-secondary-500 transition-colors" />
                </div>
                <div>
                  <label for="bulk-sync-aws-day" class="block text-xs text-secondary-400 mb-1">Day</label>
                  <input type="text" id="bulk-sync-aws-day" bind:value={awsBulkSyncForm.dayOfMonth} placeholder="*" class="w-full border border-secondary-700 rounded-lg px-3 py-2.5 text-sm text-white placeholder-secondary-500 focus:ring-2 focus:ring-secondary-500 focus:border-secondary-500 transition-colors" />
                </div>
                <div>
                  <label for="bulk-sync-aws-month" class="block text-xs text-secondary-400 mb-1">Month</label>
                  <input type="text" id="bulk-sync-aws-month" bind:value={awsBulkSyncForm.month} placeholder="*" class="w-full border border-secondary-700 rounded-lg px-3 py-2.5 text-sm text-white placeholder-secondary-500 focus:ring-2 focus:ring-secondary-500 focus:border-secondary-500 transition-colors" />
                </div>
                <div>
                  <label for="bulk-sync-aws-weekday" class="block text-xs text-secondary-400 mb-1">Weekday</label>
                  <input type="text" id="bulk-sync-aws-weekday" bind:value={awsBulkSyncForm.dayOfWeek} placeholder="*" class="w-full border border-secondary-700 rounded-lg px-3 py-2.5 text-sm text-white placeholder-secondary-500 focus:ring-2 focus:ring-secondary-500 focus:border-secondary-500 transition-colors" />
                </div>
              </div>
              <div class="text-xs text-secondary-500 mt-3 space-y-1">
                <p class="font-medium text-secondary-400">Examples:</p>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-1">
                  <span><code class="bg-secondary-700 px-1.5 py-0.5 rounded text-secondary-400">0 6 * * *</code> → Daily at 6:00 AM</span>
                  <span><code class="bg-secondary-700 px-1.5 py-0.5 rounded text-secondary-400">0 2 * * *</code> → Daily at 2:00 AM</span>
                  <span><code class="bg-secondary-700 px-1.5 py-0.5 rounded text-secondary-400">30 8 * * *</code> → Daily at 8:30 AM</span>
                  <span><code class="bg-secondary-700 px-1.5 py-0.5 rounded text-secondary-400">0 0 * * 0</code> → Weekly on Sunday</span>
                </div>
              </div>
            </div>

            <div>
              <label for="bulk-sync-aws-timezone" class="block text-sm font-medium text-secondary-300 mb-2">
                <div class="flex items-center gap-2">
                  <svg class="w-4 h-4 text-secondary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Timezone
                </div>
              </label>
              <input type="text" id="bulk-sync-aws-timezone" bind:value={awsBulkSyncForm.timezone} placeholder="UTC" class="w-full sm:max-w-xs border border-secondary-700 rounded-lg px-3 py-2.5 text-sm text-white placeholder-secondary-500 focus:ring-2 focus:ring-secondary-500 focus:border-secondary-500 transition-colors" />
              <p class="text-xs text-secondary-500 mt-2">e.g. UTC, America/New_York</p>
            </div>
          </div>

          <hr class="border-t border-secondary-800" />
          <div class="px-4 sm:px-6 py-4 space-y-3">
            {#if awsBulkSyncForm.message}
              <div class={`flex items-center gap-2 text-sm ${awsBulkSyncForm.messageType === "success" ? "text-green-600" : "text-red-600"}`}>
                {#if awsBulkSyncForm.messageType === "success"}
                  <svg class="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                  </svg>
                {:else}
                  <svg class="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                  </svg>
                {/if}
                <span>{awsBulkSyncForm.message}</span>
              </div>
            {/if}
          </div>
          {/if}
        </div>

        <div class="px-4 sm:px-6 py-4">
          <Button
            size="lg"
            disabled={bulkSyncForm.saving || awsBulkSyncForm.saving || bulkSyncForm.loading || awsBulkSyncForm.loading}
            on:click={saveAllBulkSyncConfigs}
          >
            {#if bulkSyncForm.saving || awsBulkSyncForm.saving}
              <svg class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Saving...
            {:else}
              Save Schedule
            {/if}
          </Button>
        </div>
      </div>
    {/if}

    {#if activeTab === "cleanup"}
      <div class="flex flex-col gap-4">
        <div class="overflow-hidden">
          <div class="px-4 sm:px-6 py-4 sm:py-5 border-b border-secondary-800">
            <div class="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
              <div class="flex items-center gap-3">
                <div class="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                  <svg class="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 6h18M8 6V4h8v2m-9 0l1 14h8l1-14" />
                  </svg>
                </div>
                <div>
                  <h4 class="text-base sm:text-lg font-semibold text-white">Finished Opportunities Cleanup</h4>
                  <p class="text-sm text-secondary-400">Remove Closed Lost and Launched opportunities</p>
                </div>
              </div>

              <div class="flex items-center gap-3 self-end sm:self-auto">
                <span class="text-sm text-secondary-400">{cleanupSchedulerForm.enabled ? 'Enabled' : 'Disabled'}</span>
                <button
                  type="button"
                  class={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${cleanupSchedulerForm.enabled ? 'bg-primary-600' : 'bg-secondary-600'}`}
                  on:click={() => (cleanupSchedulerForm = { ...cleanupSchedulerForm, enabled: !cleanupSchedulerForm.enabled })}
                >
                  <span class="sr-only">Enable cleanup scheduler</span>
                  <span
                    class={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-secondary-800 shadow ring-0 transition duration-200 ease-in-out ${cleanupSchedulerForm.enabled ? 'translate-x-5' : 'translate-x-0'}`}
                  ></span>
                </button>
              </div>
            </div>
          </div>

          {#if !cleanupSchedulerForm.loading}
            <div class="px-4 sm:px-6 py-4 space-y-4">
              <div>
                <div class="block text-sm font-medium text-secondary-300 mb-2">
                  <div class="flex items-center gap-2">
                    <svg class="w-4 h-4 text-secondary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Schedule (Cron)
                  </div>
                </div>
                <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 sm:gap-3">
                  <div>
                    <label for="cleanup-minute" class="block text-xs text-secondary-400 mb-1">Minute</label>
                    <input type="text" id="cleanup-minute" bind:value={cleanupSchedulerForm.minute} placeholder="*" class="w-full border border-secondary-700 rounded-lg px-3 py-2.5 text-sm text-white placeholder-secondary-500 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors" />
                  </div>
                  <div>
                    <label for="cleanup-hour" class="block text-xs text-secondary-400 mb-1">Hour</label>
                    <input type="text" id="cleanup-hour" bind:value={cleanupSchedulerForm.hour} placeholder="*" class="w-full border border-secondary-700 rounded-lg px-3 py-2.5 text-sm text-white placeholder-secondary-500 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors" />
                  </div>
                  <div>
                    <label for="cleanup-day" class="block text-xs text-secondary-400 mb-1">Day</label>
                    <input type="text" id="cleanup-day" bind:value={cleanupSchedulerForm.dayOfMonth} placeholder="*" class="w-full border border-secondary-700 rounded-lg px-3 py-2.5 text-sm text-white placeholder-secondary-500 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors" />
                  </div>
                  <div>
                    <label for="cleanup-month" class="block text-xs text-secondary-400 mb-1">Month</label>
                    <input type="text" id="cleanup-month" bind:value={cleanupSchedulerForm.month} placeholder="*" class="w-full border border-secondary-700 rounded-lg px-3 py-2.5 text-sm text-white placeholder-secondary-500 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors" />
                  </div>
                  <div>
                    <label for="cleanup-weekday" class="block text-xs text-secondary-400 mb-1">Weekday</label>
                    <input type="text" id="cleanup-weekday" bind:value={cleanupSchedulerForm.dayOfWeek} placeholder="*" class="w-full border border-secondary-700 rounded-lg px-3 py-2.5 text-sm text-white placeholder-secondary-500 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors" />
                  </div>
                </div>
              </div>

              <div>
                <label for="cleanup-timezone" class="block text-sm font-medium text-secondary-300 mb-2">Timezone</label>
                <input type="text" id="cleanup-timezone" bind:value={cleanupSchedulerForm.timezone} placeholder="UTC" class="w-full sm:max-w-xs border border-secondary-700 rounded-lg px-3 py-2.5 text-sm text-white placeholder-secondary-500 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors" />
                <p class="text-xs text-secondary-500 mt-2">e.g. UTC, America/New_York</p>
              </div>

              {#if cleanupSchedulerForm.message}
                <div class={`flex items-center gap-2 text-sm ${cleanupSchedulerForm.messageType === "success" ? "text-green-600" : "text-red-600"}`}>
                  {#if cleanupSchedulerForm.messageType === "success"}
                    <svg class="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                    </svg>
                  {:else}
                    <svg class="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                    </svg>
                  {/if}
                  <span>{cleanupSchedulerForm.message}</span>
                </div>
              {/if}
            </div>
          {/if}
        </div>

        <div class="px-4 sm:px-6 py-4 flex flex-wrap justify-start gap-3">
          <Button
            size="lg"
            disabled={cleanupSchedulerForm.saving || cleanupSchedulerForm.loading}
            on:click={() => saveBulkSyncConfig("cleanup")}
          >
            {cleanupSchedulerForm.saving ? "Working..." : "Save Cleanup Schedule"}
          </Button>
        </div>
      </div>
    {/if}

    {#if activeTab === "hubspotScheduler"}
      <div class="flex flex-col gap-4">
        <div class="overflow-hidden">
          {#if !hubspotSchedulerForm.synced}
            <div class="px-4 sm:px-6 py-3 bg-yellow-50 border-b border-yellow-300 rounded-t-lg">
              <div class="flex flex-wrap items-center justify-between gap-2">
                <div class="flex items-center gap-2 text-yellow-900">
                  <svg class="w-4 h-4 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                  </svg>
                  <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800">
                    Config out of sync
                  </span>
                  <span class="text-xs text-amber-300">
                    Running: <code class="bg-yellow-100 px-1 rounded">{hubspotSchedulerForm.runningSchedule || 'not set'}</code>
                    ({hubspotSchedulerForm.runningEnabled ? 'enabled' : 'disabled'})
                  </span>
                </div>
                <span class="text-xs text-amber-300">Save to apply changes.</span>
              </div>
            </div>
          {/if}

          <div class="px-4 sm:px-6 py-4 sm:py-5 border-b border-secondary-800">
            <div class="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
              <div class="flex items-center gap-3">
                <div class="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                  <svg class="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </div>
                <div>
                  <h4 class="text-base sm:text-lg font-semibold text-white">HubSpot Sync Scheduler</h4>
                  <p class="text-sm text-secondary-400">Poll changed HubSpot deals and run validation + AWS sync feedback loop</p>
                </div>
              </div>

              <div class="flex items-center gap-3 self-end sm:self-auto">
                <span class="text-sm text-secondary-400">{hubspotSchedulerForm.enabled ? 'Enabled' : 'Disabled'}</span>
                <button
                  type="button"
                  class={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${hubspotSchedulerForm.enabled ? 'bg-primary-600' : 'bg-secondary-600'}`}
                  on:click={() => (hubspotSchedulerForm = { ...hubspotSchedulerForm, enabled: !hubspotSchedulerForm.enabled })}
                >
                  <span class="sr-only">Enable scheduler</span>
                  <span
                    class={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-secondary-800 shadow ring-0 transition duration-200 ease-in-out ${hubspotSchedulerForm.enabled ? 'translate-x-5' : 'translate-x-0'}`}
                  ></span>
                </button>
              </div>
            </div>
          </div>

          {#if !hubspotSchedulerForm.loading}
            <div class="px-4 sm:px-6 py-4 space-y-4">
              <div>
                <div class="block text-sm font-medium text-secondary-300 mb-2">
                  <div class="flex items-center gap-2">
                    <svg class="w-4 h-4 text-secondary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Schedule (Cron)
                  </div>
                </div>
                <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 sm:gap-3">
                  <div>
                    <label for="hubspot-scheduler-minute" class="block text-xs text-secondary-400 mb-1">Minute</label>
                    <input type="text" id="hubspot-scheduler-minute" bind:value={hubspotSchedulerForm.minute} placeholder="*" class="w-full border border-secondary-700 rounded-lg px-3 py-2.5 text-sm text-white placeholder-secondary-500 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors" />
                  </div>
                  <div>
                    <label for="hubspot-scheduler-hour" class="block text-xs text-secondary-400 mb-1">Hour</label>
                    <input type="text" id="hubspot-scheduler-hour" bind:value={hubspotSchedulerForm.hour} placeholder="*" class="w-full border border-secondary-700 rounded-lg px-3 py-2.5 text-sm text-white placeholder-secondary-500 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors" />
                  </div>
                  <div>
                    <label for="hubspot-scheduler-day" class="block text-xs text-secondary-400 mb-1">Day</label>
                    <input type="text" id="hubspot-scheduler-day" bind:value={hubspotSchedulerForm.dayOfMonth} placeholder="*" class="w-full border border-secondary-700 rounded-lg px-3 py-2.5 text-sm text-white placeholder-secondary-500 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors" />
                  </div>
                  <div>
                    <label for="hubspot-scheduler-month" class="block text-xs text-secondary-400 mb-1">Month</label>
                    <input type="text" id="hubspot-scheduler-month" bind:value={hubspotSchedulerForm.month} placeholder="*" class="w-full border border-secondary-700 rounded-lg px-3 py-2.5 text-sm text-white placeholder-secondary-500 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors" />
                  </div>
                  <div>
                    <label for="hubspot-scheduler-weekday" class="block text-xs text-secondary-400 mb-1">Weekday</label>
                    <input type="text" id="hubspot-scheduler-weekday" bind:value={hubspotSchedulerForm.dayOfWeek} placeholder="*" class="w-full border border-secondary-700 rounded-lg px-3 py-2.5 text-sm text-white placeholder-secondary-500 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors" />
                  </div>
                </div>
              </div>

              <div>
                <label for="hubspot-scheduler-timezone" class="block text-sm font-medium text-secondary-300 mb-2">
                  <div class="flex items-center gap-2">
                    <svg class="w-4 h-4 text-secondary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Timezone
                  </div>
                </label>
                <input type="text" id="hubspot-scheduler-timezone" bind:value={hubspotSchedulerForm.timezone} placeholder="UTC" class="w-full sm:max-w-xs border border-secondary-700 rounded-lg px-3 py-2.5 text-sm text-white placeholder-secondary-500 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors" />
                <p class="text-xs text-secondary-500 mt-2">e.g. UTC, America/New_York</p>
              </div>
            </div>

            <hr class="border-t border-secondary-800" />
            <div class="px-4 sm:px-6 py-4 space-y-3">
              {#if hubspotSchedulerForm.message}
                <div class={`flex items-center gap-2 text-sm ${hubspotSchedulerForm.messageType === "success" ? "text-green-600" : "text-red-600"}`}>
                  {#if hubspotSchedulerForm.messageType === "success"}
                    <svg class="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                    </svg>
                  {:else}
                    <svg class="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                    </svg>
                  {/if}
                  <span>{hubspotSchedulerForm.message}</span>
                </div>
              {/if}
            </div>
          {/if}
        </div>

        <div class="px-4 sm:px-6 py-4 flex flex-wrap justify-start gap-3">
          <Button
            variant="secondary"
            size="lg"
            disabled={hubspotSchedulerForm.saving || hubspotSchedulerForm.loading}
            on:click={runHubspotSchedulerNow}
          >
            Run Now
          </Button>
          <Button
            size="lg"
            disabled={hubspotSchedulerForm.saving || hubspotSchedulerForm.loading}
            on:click={saveHubspotSchedulerConfig}
          >
            {#if hubspotSchedulerForm.saving}
              <svg class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Working...
            {:else}
              Save Scheduler
            {/if}
          </Button>
        </div>
      </div>
    {/if}

    {#if activeTab !== "bulksync" && activeTab !== "hubspotScheduler" && activeTab !== "cleanup" && activeTab !== "hubspotCard"}
      {#if saveMessage}
        <div
          class={`rounded border p-3 text-sm ${
            saveMessageType === "success"
              ? "border-green-500/40 bg-green-500/10 text-green-300"
              : "border-red-500/40 bg-red-500/10 text-red-300"
          }`}
        >
          {saveMessage}
        </div>
      {/if}

      <div class="mt-6 grid grid-cols-2 gap-3 border-t border-secondary-800 pt-5 sm:flex sm:justify-start">
        <Button type="submit" size="lg" disabled={isLoading}>
          {isLoading ? "Saving..." : "Save"}
        </Button>
        <Button variant="secondary" size="lg" on:click={() => (confirmReset = true)} disabled={isLoading}>
          Reset
        </Button>
      </div>
    {/if}
  </form>
  </div>
</div>

<style>
  .settings-shell :global(code) {
    background-color: #343a52 !important;
    color: #dbe3f0 !important;
  }

  .settings-shell :global(.bg-secondary-800) {
    background-color: #24283a !important;
  }
  .settings-shell :global(.bg-secondary-900) {
    background-color: #1e2233 !important;
  }
  .settings-shell :global(.bg-secondary-700),
  .settings-shell :global(.hover\:bg-secondary-700:hover) {
    background-color: #343a52 !important;
  }
  .settings-shell :global(.bg-secondary-600) {
    background-color: #3d425b !important;
  }
  .settings-shell :global(.border-secondary-800) {
    border-color: #2c3145 !important;
  }
  .settings-shell :global(.border-secondary-700) {
    border-color: #383e55 !important;
  }
  .settings-shell :global(.border-secondary-600) {
    border-color: #444a63 !important;
  }

  .settings-shell :global(input:not([type="checkbox"])),
  .settings-shell :global(select),
  .settings-shell :global(textarea) {
    background-color: #1a1e2f;
    color: #f1f5f9;
    border-color: #383e55;
    color-scheme: dark;
  }

  .settings-shell :global(input::placeholder),
  .settings-shell :global(textarea::placeholder) {
    color: #64748b;
  }

  .settings-shell {
    display: grid;
    grid-template-columns: 280px minmax(0, 1fr);
    min-height: calc(100vh - 170px);
    border: 1px solid rgba(226, 232, 240, 0.16);
    border-radius: 0.75rem;
    background: #24283a;
    box-shadow: 0 1px 2px rgb(0 0 0 / 0.05);
    overflow-x: auto;
  }

  .settings-shell :global(.settings-content) {
    min-width: 0;
    overflow: hidden;
  }

  .settings-shell.collapsed-settings-nav {
    grid-template-columns: 88px minmax(0, 1fr);
  }

  .settings-shell.collapsed-settings-nav :global(.settings-nav-item) {
    justify-content: center;
    padding-left: 0.75rem;
    padding-right: 0.75rem;
  }

  .settings-shell.collapsed-settings-nav :global(.settings-nav-copy) {
    display: none;
  }

  .settings-shell.collapsed-settings-nav :global(.settings-bottom-toggle) {
    margin-left: auto;
    margin-right: auto;
  }

  @media (max-width: 767px) {
    .settings-shell {
      grid-template-columns: 112px minmax(0, 1fr);
    }

    .settings-shell.collapsed-settings-nav {
      grid-template-columns: 56px minmax(0, 1fr);
    }

    .settings-shell :global(.settings-nav-description) {
      display: none;
    }

    .settings-shell :global(.settings-nav-item) {
      gap: 0.5rem;
      min-width: 0;
      overflow: hidden;
      padding-left: 0.375rem;
      padding-right: 0.375rem;
    }

    .settings-shell :global(.settings-nav-label) {
      display: inline-block;
      max-width: 3.25rem;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-size: 0.8125rem;
    }

    .settings-shell :global(.settings-nav-copy) {
      min-width: 0;
      overflow: hidden;
    }

    .settings-shell :global(.settings-bottom-toggle) {
      margin-left: auto;
      margin-right: auto;
    }
  }
</style>

<!-- Reset Confirmation -->
{#if confirmReset}
  <div
    class="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50"
  >
    <div class="bg-secondary-800 rounded-xl shadow-lg p-6 max-w-sm w-full text-center">
      <h2 class="mb-2 text-lg font-semibold" style="color: var(--ace-text, #f8fafc)">Reset Configuration</h2>
      <p class="text-secondary-400">Are you sure you want to reset to defaults?</p>
      <div class="flex justify-center gap-4 mt-4">
        <Button variant="danger" on:click={confirmAndReset}>Yes, Reset</Button>
        <Button variant="secondary" on:click={() => (confirmReset = false)}>Cancel</Button>
      </div>
    </div>
  </div>
{/if}

<MessageModal
  show={confirmCatalogSwitch}
  title="Change AWS Catalog"
  message="Changing the AWS catalog will delete all local opportunity data and related records from the internal database. This prevents Sandbox and Production catalog records from being mixed or synced into the wrong AWS catalog."
  variant="danger"
  confirmLabel="Delete Local Data & Save"
  cancelLabel="Cancel"
  onConfirm={confirmAndSaveCatalogSwitch}
  onClose={() => (confirmCatalogSwitch = false)}
/>
